import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { messageDErreur, useSession } from '../auth/session';
import { cleSourceConges } from '../compte/SourceConges';
import { Alerte, Bouton, Champ } from '../ui';
import { ChampDateNaissance } from '../ui/ChampDateNaissance';
import type { EnfantDuDossier } from './types';

/**
 * Corriger le prénom ou la date de naissance d'un enfant.
 *
 * **Corriger n'est pas réécrire l'histoire.** L'immuabilité du dossier protège ce qui a été
 * *dit* — un message, une entrée de journal, une dépense déclarée. La date de naissance
 * d'un enfant n'est pas une déclaration : c'est un fait extérieur au dossier, et une faute
 * de frappe n'en fait pas un autre enfant. Sans cet écran, l'âge affiché sur tous les
 * écrans resterait faux pour toujours.
 *
 * L'écran le dit, plutôt que de le laisser deviner : la correction est visible des deux
 * parents au registre. Ce n'est pas une menace, c'est ce qui permet à l'autre de ne pas
 * s'inquiéter d'un changement qu'il n'a pas fait.
 */
export function CorrigerEnfant({
  caseFileId,
  enfant,
}: {
  caseFileId: string;
  enfant: EnfantDuDossier;
}) {
  const { client } = useSession();
  const queryClient = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [prenom, setPrenom] = useState(enfant.firstName);
  const [naissance, setNaissance] = useState(enfant.birthDate.slice(0, 10));

  const corriger = useMutation({
    mutationFn: () =>
      client.request(`/case-files/${caseFileId}/children/${enfant.id}`, {
        method: 'PATCH',
        body: { firstName: prenom.trim(), birthDate: naissance },
      }),
    onSuccess: () => {
      setOuvert(false);
      void queryClient.invalidateQueries({ queryKey: ['case-file', caseFileId] });
      void queryClient.invalidateQueries({ queryKey: ['case-files'] });
      // Le prénom corrigé se lit aussi dans la légende des congés du calendrier, dont la
      // source est gardée trente minutes : sans cette ligne, elle continuerait d'y
      // afficher l'ancien jusqu'au prochain rechargement.
      void queryClient.invalidateQueries({ queryKey: cleSourceConges(caseFileId) });
    },
  });

  if (!ouvert) {
    return (
      <Bouton variante="lien" onClick={() => setOuvert(true)}>
        Corriger son prénom ou sa date de naissance
      </Bouton>
    );
  }

  return (
    <form
      className="corriger-enfant"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        corriger.mutate();
      }}
    >
      {corriger.isError ? (
        <Alerte titre="La correction n’a pas pu être enregistrée">
          {messageDErreur(corriger.error)}
        </Alerte>
      ) : null}

      <Champ
        label="Prénom"
        value={prenom}
        onChange={(evenement) => setPrenom(evenement.target.value)}
        maxLength={80}
        required
      />
      <ChampDateNaissance
        label="Date de naissance"
        value={naissance}
        onChange={setNaissance}
        aide="La correction apparaît au registre du dossier : l'autre parent voit ce qui a changé, et par qui."
        required
      />

      <div className="corriger-enfant-actions">
        <Bouton type="submit" disabled={!prenom.trim() || !naissance || corriger.isPending}>
          {corriger.isPending ? 'Enregistrement…' : 'Corriger'}
        </Bouton>
        <Bouton variante="secondaire" onClick={() => setOuvert(false)}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
